import type { SurveyDay, SurveyWeek } from './report';

/*
 * Formatting and week helpers for the Système U dashboard.
 *
 * The labels come in English or French (i18n.ts), but every figure is
 * French-formatted in both: the audience is a French retailer, and the design
 * writes "412 318", "77,3 %" and "6 min 04 s" under English labels too. Intl's
 * fr-FR output uses narrow no-break spaces, which also keeps a number from
 * wrapping across two lines.
 */

const NBSP = '\u00a0';

export function fmtInt(n: number | null | undefined): string {
  if (n == null) return '—';
  return Math.round(n).toLocaleString('fr-FR');
}

/** 77.27 → "77,3 %". */
export function fmtPct(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}${NBSP}%`;
}

/** 2.5527 → "2,55". */
export function fmtDec(n: number | null | undefined, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Seconds → "28 940 h" (whole hours). */
export function fmtHours(sec: number | null | undefined): string {
  if (sec == null) return '—';
  return `${fmtInt(sec / 3600)}${NBSP}h`;
}

/** Seconds → "6 min 04 s", or "1 h 02 min" from an hour up. */
export function fmtDuration(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return '—';
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s}${NBSP}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}${NBSP}min ${String(s % 60).padStart(2, '0')}${NBSP}s`;
  return `${Math.floor(m / 60)}${NBSP}h ${String(m % 60).padStart(2, '0')}${NBSP}min`;
}

/** Axis ticks: 150000 → "150 k". */
export function fmtCompact(n: number): string {
  if (n >= 1000) return `${fmtInt(n / 1000)}${NBSP}k`;
  return fmtInt(n);
}

/** "2026-10-13" → "13/10/2026". */
export function fmtDate(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

/**
 * A calendar day written out for the client, in the page's locale:
 * "long" → "Friday 30 October 2026" / "vendredi 30 octobre 2026",
 * "short" → "Fri 30 Oct" / "ven. 30 oct.", "dayMonth" → "30 Oct" / "30 oct.".
 * The date is a calendar day, so it is read in UTC and never shifts.
 */
export function fmtDay(iso: string, locale: string, style: 'long' | 'short' | 'dayMonth'): string {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  const opts: Intl.DateTimeFormatOptions =
    style === 'long'
      ? { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
      : style === 'short'
        ? { weekday: 'short', day: 'numeric', month: 'short' }
        : { day: 'numeric', month: 'short' };
  return new Intl.DateTimeFormat(locale, { ...opts, timeZone: 'UTC' }).format(d);
}

/** Two calendar days as a span: "6–12 Oct", "6 Oct – 16 Nov" ("8–13 déc." in French). */
export function fmtDayRange(from: string, to: string, locale: string): string {
  const a = new Date(`${from}T12:00:00Z`);
  const b = new Date(`${to}T12:00:00Z`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }).formatRange(a, b);
}

/** Day of the week of an ISO date, 0 = Sunday (read in UTC, like fmtDay). */
export const weekdayOf = (iso: string) => new Date(`${iso}T12:00:00Z`).getUTCDay();

/** ISO instant → "21:44:48" in the campaign's timezone. */
export function fmtClock(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('fr-FR', { timeZone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(d);
}

/** a ÷ b as a percentage; null when there is no base. */
export function share(part: number | null | undefined, whole: number | null | undefined): number | null {
  if (part == null || whole == null || whole <= 0) return null;
  return (part / whole) * 100;
}

/** a ÷ b; null when there is no base. */
export function ratio(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null || b == null || b <= 0) return null;
  return a / b;
}

/** The days of the given weeks (the selected period), in order. */
export function daysOfWeeks(days: SurveyDay[], weeks: SurveyWeek[]): SurveyDay[] {
  if (weeks.length === 0) return days;
  const from = weeks[0].start;
  const to = weeks[weeks.length - 1].end;
  return days.filter((d) => d.date >= from && d.date <= to);
}

/** Round "nice" ceiling for a bar axis: 142 380 → 150 000. */
export function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(max));
  const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((m) => m * pow >= max) ?? 10;
  return step * pow;
}
